import { useMemo, useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import prettyBytes from 'pretty-bytes';
import { Download, Inbox, Package } from 'lucide-react';
import { toast } from 'sonner';

import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import { EmptyState } from '@/components/EmptyState';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { MarkdownBody } from '@/features/issues/components/MarkdownBody';
import { useAuth } from '@/lib/auth/useAuth';

import { buildAssetDownloadUrl, downloadAsset, useReleases } from '../api';
import type { PortalRelease } from '../types';

const PAGE_SIZE = 50;
const ALL_PRODUCTS = '__all__';

interface GroupedReleases {
  repoName: string;
  productDisplayName: string;
  releases: PortalRelease[];
}

function groupByRepo(items: PortalRelease[]): GroupedReleases[] {
  const map = new Map<string, GroupedReleases>();
  for (const release of items) {
    const existing = map.get(release.repoName);
    if (existing) {
      existing.releases.push(release);
    } else {
      map.set(release.repoName, {
        repoName: release.repoName,
        productDisplayName: release.productDisplayName,
        releases: [release],
      });
    }
  }
  const groups = Array.from(map.values());
  groups.sort((a, b) => a.productDisplayName.localeCompare(b.productDisplayName));
  return groups;
}

// Collapsed-trigger preview: first paragraph of the notes with leading
// Markdown markers stripped so it reads as plain prose. The three-line cap
// is done in the markup with `line-clamp-3` — measuring "lines" in JS is
// unreliable across font/width, so we let CSS do it.
function previewText(body: string): string {
  const trimmed = body.trim();
  if (!trimmed) return '';
  const firstPara = trimmed.split(/\n\s*\n/, 1)[0];
  return firstPara
    .split('\n')
    .map((line) => line.replace(/^\s{0,3}(#{1,6}\s+|[-*+]\s+|>\s+)/, '').trim())
    .filter(Boolean)
    .join(' ');
}

export function ReleasesList() {
  const releases = useReleases({ limit: PAGE_SIZE, offset: 0 });
  const [productFilter, setProductFilter] = useState<string>(ALL_PRODUCTS);

  const groups = useMemo(() => groupByRepo(releases.data?.items ?? []), [releases.data?.items]);

  // Filter options come from the full group set so the dropdown stays
  // stable regardless of the current selection.
  const productOptions = useMemo(
    () => groups.map((g) => ({ repoName: g.repoName, label: g.productDisplayName })),
    [groups],
  );

  const visibleGroups =
    productFilter === ALL_PRODUCTS ? groups : groups.filter((g) => g.repoName === productFilter);

  if (releases.isLoading) {
    return (
      <div className="space-y-8" data-testid="releases-loading">
        {[0, 1].map((g) => (
          <div key={g} className="space-y-3">
            <Skeleton className="h-6 w-48" />
            <div className="space-y-3">
              {[0, 1, 2].map((r) => (
                <Skeleton key={r} className="h-20 w-full" />
              ))}
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (releases.isError) {
    return (
      <div className="rounded-md border border-destructive bg-destructive/10 p-4 text-sm">
        <div className="text-destructive">Couldn&apos;t load releases.</div>
        <Button variant="outline" size="sm" className="mt-2" onClick={() => releases.refetch()}>
          Retry
        </Button>
      </div>
    );
  }

  if (groups.length === 0) {
    return <EmptyState Icon={Inbox} title="No releases available yet." />;
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2">
        <span className="text-sm text-muted-foreground">Product:</span>
        <div className="w-64">
          <Select value={productFilter} onValueChange={setProductFilter}>
            <SelectTrigger data-testid="release-product-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value={ALL_PRODUCTS}>All products</SelectItem>
              {productOptions.map((p) => (
                <SelectItem
                  key={p.repoName}
                  value={p.repoName}
                  data-testid={`release-product-option-${p.repoName}`}
                >
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {visibleGroups.length === 0 ? (
        <EmptyState Icon={Inbox} title="No releases for the selected product." />
      ) : (
        visibleGroups.map((group) => (
          <section
            key={group.repoName}
            data-testid={`release-group-${group.repoName}`}
            className="space-y-3"
          >
            <h2 className="text-lg font-semibold">{group.productDisplayName}</h2>
            <Accordion type="multiple" className="space-y-2">
              {group.releases.map((release) => (
                <ReleaseAccordionItem key={release.tag} release={release} />
              ))}
            </Accordion>
          </section>
        ))
      )}
    </div>
  );
}

function ReleaseAccordionItem({ release }: { release: PortalRelease }) {
  const { accessToken } = useAuth();
  const preview = previewText(release.body);

  const handleDownload = async (assetName: string) => {
    const url = buildAssetDownloadUrl(release.repoName, release.tag, assetName);
    try {
      await downloadAsset(url, assetName, accessToken);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Download failed');
    }
  };

  return (
    <AccordionItem
      value={`${release.repoName}::${release.tag}`}
      data-testid={`release-accordion-item-${release.repoName}-${release.tag}`}
    >
      <AccordionTrigger>
        <div className="min-w-0 space-y-1">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-sm">{release.tag}</span>
            <span className="font-semibold">{release.name}</span>
            {release.isPrerelease && (
              <Badge data-testid="prerelease-badge" className="bg-amber-100 text-amber-800">
                Prerelease
              </Badge>
            )}
          </div>
          <div className="text-xs text-muted-foreground">
            Released {formatDistanceToNow(new Date(release.publishedAt), { addSuffix: true })}
          </div>
          {preview ? (
            <p className="line-clamp-3 text-sm text-foreground/70">{preview}</p>
          ) : (
            <p className="text-sm text-muted-foreground">No release notes.</p>
          )}
        </div>
      </AccordionTrigger>
      <AccordionContent>
        <div className="space-y-4">
          {release.body.trim() ? (
            <MarkdownBody>{release.body}</MarkdownBody>
          ) : (
            <div className="text-sm text-muted-foreground">No release notes.</div>
          )}

          {release.assets.length > 0 && (
            <div>
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                Assets
              </div>
              <ul className="space-y-1">
                {release.assets.map((asset) => (
                  <li
                    key={asset.name}
                    className="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm"
                    data-testid={`asset-row-${asset.name}`}
                  >
                    <div className="flex items-center gap-2 truncate">
                      <Package className="h-4 w-4 shrink-0 text-slate-400" aria-hidden="true" />
                      <span className="truncate font-mono text-xs">{asset.name}</span>
                    </div>
                    <div className="flex items-center gap-3 text-xs text-muted-foreground">
                      <span>{prettyBytes(asset.sizeBytes)}</span>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownload(asset.name)}
                        aria-label={`Download ${asset.name}`}
                      >
                        <Download className="mr-1 h-3.5 w-3.5" aria-hidden="true" />
                        Download
                      </Button>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </AccordionContent>
    </AccordionItem>
  );
}
